export * from './olmap';
export * from './labels';
export * from './ai-modules';

/**
 * @date 2024/06/14
 */
export const LOGROCKET_APP_ID = 'k96uuh/taksoai';

export const PROJECT_SETTING_COUNTRY = 'CA'; // or US

// ====== routes config section ======
export const TOKEN_UNAVAILABLE_FORWARD = '/auth/login';
export const SIGN_UP_COMPLETE_FORWARD = '/auth/sign-up';
export const CONFIRM_EMAIL_FORWARD = '/auth/confirm';
export const CONFIRM_USER_INVITATION = '/auth/confirm_invitation';
export const TERMS_POLICY_FORWARD = '/auth/user-agreement-policy';
export const FORGOT_PASSWORD_FORWARD = '/auth/forgot-password';
export const RESET_PASSWORD_FORWARD = '/auth/reset-password';
export const COOKIE_POLICY_FORWARD = '/auth/cookie-policy';
export const PRIVACY_POLICY_FORWARD = '/auth/privacy-policy';

export const LOGIN_SUCCESS_ROUTE = '/home';
export const COMPANY_ADMIN_ROUTE = '/home/admin-profile';
export const USER_PROFILE_ROUTE = '/home/user-profile';
export const USER_DASHBOARD_ROUTE = '/home/dashboard';

export const USER_GUIDE_URL = 'https://taksoai.github.io/user-guide-book/';

// ====== end of routes config ======

/**
 * NOTE: local config object
 */
const mEnv = import.meta.env;
// console.log(mEnv);

export const appVersion = `${mEnv.VITE_APP_NAME}@${mEnv.VITE_APP_VERSION}`;

export const PRODUCT_RELEASE_VERSION = `TaksoAI v${mEnv.VITE_APP_VERSION} @2026`;

/**
 * get taksoai company id for `ADMIN` role checking
 * @2025/10/01
 */
export const ADMIN_PANEL_OWNER = mEnv.VITE_APP_TAKSOAI_COMPANY_ID as string;

/**
 * WebSocket Server URL
 *
 * @date 2024/02/20
 */
export const WS_URL = mEnv.VITE_APP_WS_URL as string;

/**
 * Auth module Base URL, just a domain set from environment file(assigned in Vercel)
 */
export const API_URL = mEnv.VITE_APP_API_URL as string;

/**
 * development env: /v1
 * production env in master branch: /prod
 */
export const API_CTX = mEnv.VITE_APP_API_CTX as string;
export const APP_DEBUG = mEnv.VITE_APP_DEBUG_MODE as string;
export const APP_DOMAIN = mEnv.VITE_APP_DOMAIN as string;
export const APP_ENABLE_SENTRY = mEnv.VITE_APP_ENABLE_SENTRY as string;
export const APP_ENABLE_INSULATION = mEnv.VITE_APP_ENABLE_INSULATION as string;

export const RENDERING_ENHANCE = mEnv.VITE_APP_REN_ENHANCE as string;
export const APP_MEM_TRACING = mEnv.VITE_APP_MEM_TRACING as string;
export const CATALOGUES_DURATION = mEnv.VITE_APP_CATALOGUES_DURATION as string;
export const AUTO_SAVING_DISABILITY = mEnv.VITE_APP_DIABLE_AUTO_SAVING;
export const LOADING_ANIMATION = mEnv.VITE_APP_LOADING_ANIMATION_TYPE as string;

/**
 * NOTE: this(`NODE_ENV`) is build parameter from building command line:
 */
export const isVercelEnv = import.meta.env.PROD;
export const isProdEnv = API_CTX === '/prod';
export const isDebugMode = APP_DEBUG === 'true';
export const isRenderingEnhanceMode = RENDERING_ENHANCE === 'true';
export const isUsingSentry = APP_ENABLE_SENTRY === 'true';
export const isUsingMemTracing = APP_MEM_TRACING === 'true';
export const isAutoSavingDisabled = AUTO_SAVING_DISABILITY === 'true';
export const isUseScanner = LOADING_ANIMATION == 'scanner';
export const isUsingInsulation = APP_ENABLE_INSULATION == 'true';

/**
 * `Project` feature(module) API path
 */
export const TK_API_PROJECTS = `${API_CTX}/projects`;
export const TK_API_ITEMS = `${API_CTX}/items`;
export const TK_API_PROPERTIES = `${API_CTX}/properties`;
/** added at 2025/04/24 */
export const TK_API_AIMODULES = `${API_CTX}/ai-modules`;
/**
 * Estimating-software export API (`taksoai-ai-api`, `/v1/integration-exports`
 * family) - target systems / inspections / exports / company templates.
 * @date 2026/10/27
 */
export const TK_API_COMPANIES = `${API_CTX}/companies`;
export const TK_API_INTEGRATION_SOFTWARE = `${API_CTX}/integration-software`;
export const TK_API_INTEGRATION_INSPECTIONS = `${API_CTX}/integration-inspections`;
export const TK_API_INTEGRATION_EXPORT_REQUESTS = `${API_CTX}/integration-export-requests`;
export const TK_USER_ME = `${API_CTX}/users/me`;
export const STATES_OF_COUNTRY = `${API_CTX}/countries/${PROJECT_SETTING_COUNTRY}/states`;
export const STATES_OF_NORTHAMERICA = `${API_CTX}/states`;
export const CONSTRUCTION_SECTORS = `${API_CTX}/construction-sectors`;
export const PROJECT_TYPES = `${API_CTX}/project-types`;
export const PROJECT_STAGES = `${API_CTX}/project-stages`;
export const zoomifyBaseURL = `${API_URL}${API_CTX}/`;

export const useScreenWidth =
  process.env.VITE_APP_ADAPT_SCREEN_WIDTH === 'true';

// ====== insulation area constants ======
export const DEFAULT_AREA_ID = '__default_area__';

export const JWT_SECRET = '123456' as string;
export const PROJECT_COUNT_ONE_PAGE = 20;
export const PROJECTS_GALLERY_SLIDES = 15;
export const TABS_OPEN_LIMIT = 5;
export const MAX_SINGLE_UPLOAD = 6;
export const INPUT_LENGTH_LIMIT = 36;
export const INPUT_LENGTH_SHORT = 12;
export const TEXTAREA_LENGTH_LIMIT = 255;

export const SLIDE_SHOW_COUNTS = 4;

/**
 * - 0.037, 100 / 45 * 60, 100% completed in 45 mins
 * - 0.04, 100 / 40 * 60, 100% completed in 40 mins
 */
export const JOB_AUTO_PROGRESS_TICK = 0.037;
/**
 * scheduled auto-saving in seconds
 */
export const AUTO_SAVING_AFTER = 10;

export const TOKEN_EXPIRY_LENGTH = 24 * 3600 * 1000; // in miliseconds

/**
 * axios timeout length, in miliseconds
 * @date 2024/04/17
 */
export const NETWORK_TIMETOUT_LIMIT = 60 * 1000;

/**
 * pdf file extraction checking interval by miliseconds
 */
export const PDF_FILE_EXTRACTION_CHECKING_INTERVAL = 10 * 1000; // in miliseconds
